'use client';

import { c as _c } from "react/compiler-runtime";
import { FlagSolid, HomeSolid, InfoCircle, Minus, Plus, StayPictogram } from '@byway/icons';
import clsx from 'clsx';
import { format, isSameMonth, parseISO } from 'date-fns';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import nightStepperStyles from '~/components/ItineraryCard/src/AccommodationCardNightStepper.module.css';
import stepperStyles from '~/components/NumericStepper/src/NumericStepper.module.css';
import type { TripOverviewLeg, TripOverviewStay } from '~/services/engine-ts';
import { ITINERARY_ITEM_ID_PREFIX } from '~/stores/tpv2/overviewProgressStore';
import { getDateFnsLocale } from '~/utils/dateHelpers/dateHelpers';
import { usePlaceNames } from '../hooks/usePlaceNames';
import { useStayNightsStepper } from '../hooks/useStayNightsStepper';
import { useTripOverviewSummary } from '../hooks/useTripOverviewSummary';
import { localizeOverviewItems } from '../lib/localizePlaceNames';
import { scrollItineraryCardIntoView } from '../lib/scrollItineraryCardIntoView';
import styles from './TripOverviewStops.module.css';
const BoundaryRow = t0 => {
  const $ = _c(12);
  const {
    name,
    label,
    icon
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = clsx(styles.icon, styles.iconHome);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== icon) {
    t2 = <span className={t1}>{icon}</span>;
    $[1] = icon;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== name) {
    t3 = <span className={styles.name}>{name}</span>;
    $[3] = name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = clsx(styles.meta, styles.boundaryMeta);
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== label) {
    t5 = <span className={t4}>{label}</span>;
    $[6] = label;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t5) {
    t6 = <li className={styles.row}>{t2}{t3}{t5}</li>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
type StayRowProps = {
  stay: TripOverviewStay;
  tripId: string;
  dateRange: string | null;
};
const StayRow = t0 => {
  const $ = _c(60);
  const {
    stay,
    tripId,
    dateRange
  } = t0;
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  const place = stay.place.name;
  let t1;
  if ($[0] !== stay.itemId || $[1] !== stay.nights || $[2] !== tripId) {
    t1 = {
      nights: stay.nights,
      tripId,
      stayItemId: stay.itemId
    };
    $[0] = stay.itemId;
    $[1] = stay.nights;
    $[2] = tripId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    nights,
    errorMessage,
    isCommitting,
    canDecrement,
    canIncrement,
    decrement,
    increment
  } = useStayNightsStepper(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className={styles.icon}><StayPictogram color="white" size="small" decorative={true} /></span>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `#${ITINERARY_ITEM_ID_PREFIX}${stay.itemId}`;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = clsx(styles.name, styles.nameLink);
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== stay.itemId) {
    t5 = event => {
      if (scrollItineraryCardIntoView(stay.itemId)) {
        event.preventDefault();
      }
    };
    $[6] = stay.itemId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== place || $[9] !== t3 || $[10] !== t5) {
    t6 = <a href={t3} className={t4} onClick={t5}>{place}</a>;
    $[8] = place;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = clsx(stepperStyles.button, styles.stepButton);
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = !canDecrement;
  let t9;
  if ($[13] !== place || $[14] !== tAria) {
    t9 = tAria("decrease_nights_at", {
      place
    });
    $[13] = place;
    $[14] = tAria;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Minus decorative={true} strokeWidth={2} />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== decrement || $[18] !== t8 || $[19] !== t9) {
    t11 = <button type="button" className={t7} data-ua-click="adjust-length-stay" disabled={t8} onClick={decrement} aria-label={t9}>{t10}</button>;
    $[17] = decrement;
    $[18] = t8;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== nights || $[22] !== t) {
    t12 = t("nights_count", {
      count: nights
    });
    $[21] = nights;
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== nights || $[25] !== t12) {
    t13 = <output aria-live="polite" aria-label={t12} className={styles.count}>{nights}</output>;
    $[24] = nights;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = clsx(stepperStyles.button, styles.stepButton);
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = !canIncrement;
  let t16;
  if ($[28] !== place || $[29] !== tAria) {
    t16 = tAria("increase_nights_at", {
      place
    });
    $[28] = place;
    $[29] = tAria;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Plus decorative={true} strokeWidth={2} />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== increment || $[33] !== t15 || $[34] !== t16) {
    t18 = <button type="button" className={t14} data-ua-click="adjust-length-stay" disabled={t15} onClick={increment} aria-label={t16}>{t17}</button>;
    $[32] = increment;
    $[33] = t15;
    $[34] = t16;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t11 || $[37] !== t13 || $[38] !== t18) {
    t19 = <div className={styles.stepper}>{t11}{t13}{t18}</div>;
    $[36] = t11;
    $[37] = t13;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== nights || $[41] !== t) {
    t20 = t("nights_count", {
      count: nights
    });
    $[40] = nights;
    $[41] = t;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t20) {
    t21 = <span className={styles.nights}>{t20}</span>;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== dateRange) {
    t22 = dateRange ? <span className={styles.dates}>{dateRange}</span> : null;
    $[45] = dateRange;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t21 || $[48] !== t22) {
    t23 = <span className={styles.meta}>{t21}{t22}</span>;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== isCommitting) {
    t24 = isCommitting ? <span className={clsx(nightStepperStyles.loadingBar, styles.loadingBar)} aria-hidden="true" /> : null;
    $[50] = isCommitting;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== errorMessage) {
    t25 = errorMessage ? <p className={styles.error}><InfoCircle decorative={true} size="small" />{errorMessage}</p> : null;
    $[52] = errorMessage;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t19 || $[55] !== t23 || $[56] !== t24 || $[57] !== t25 || $[58] !== t6) {
    t26 = <li className={styles.row}>{t2}{t6}{t19}{t23}{t24}{t25}</li>;
    $[54] = t19;
    $[55] = t23;
    $[56] = t24;
    $[57] = t25;
    $[58] = t6;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  return t26;
};
export const TripOverviewStops = () => {
  const $ = _c(15);
  const t = useTranslations("journey");
  const locale = useLocale();
  const placeNames = usePlaceNames();
  const {
    tripId,
    overviewData,
    isDateUnset
  } = useTripOverviewSummary();
  if (!overviewData) {
    return null;
  }
  let t0;
  if ($[0] !== isDateUnset || $[1] !== locale || $[2] !== overviewData.items || $[3] !== overviewData.requestToBook || $[4] !== placeNames || $[5] !== t || $[6] !== tripId) {
    const items = localizeOverviewItems(overviewData.items, placeNames);
    const firstItem = items[0];
    const lastItem = items.at(-1);
    const firstLeg = items.find(_temp);
    const stays = items.filter(_temp2);
    const showDates = !isDateUnset && !overviewData.requestToBook;
    const dateFnsLocale = getDateFnsLocale(locale);
    const formatStayDates = stay => {
      const start = parseISO(stay.startDate);
      const end = parseISO(stay.endDate);
      const startFormat = isSameMonth(start, end) ? "d" : "d MMM";
      return `${format(start, startFormat, {
        locale: dateFnsLocale
      })} - ${format(end, "d MMM", {
        locale: dateFnsLocale
      })}`;
    };
    t0 = [firstItem?.type === "leg" && <BoundaryRow key="start" name={firstItem.origin.name} label={t("map_start")} icon={<HomeSolid color="white" size="small" decorative={true} />} />, ...stays.map(stay_0 => <StayRow key={stay_0.itemId} stay={stay_0} tripId={tripId} dateRange={showDates ? formatStayDates(stay_0) : null} />), lastItem?.type === "leg" && <BoundaryRow key="end" name={lastItem.destination.name} label={t("map_end")} icon={lastItem.destination.id === firstLeg?.origin.id ? <HomeSolid color="white" size="small" decorative={true} /> : <FlagSolid color="white" size="small" decorative={true} />} />];
    $[0] = isDateUnset;
    $[1] = locale;
    $[2] = overviewData.items;
    $[3] = overviewData.requestToBook;
    $[4] = placeNames;
    $[5] = t;
    $[6] = tripId;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  const rows = t0;
  let t1;
  if ($[8] !== t) {
    t1 = t("trip_overview");
    $[8] = t;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== rows) {
    t2 = <ol className={styles.list}>{rows}</ol>;
    $[10] = rows;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t1 || $[13] !== t2) {
    t3 = <section className={styles.card} aria-label={t1}>{t2}</section>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
function _temp(item) {
  return item.type === "leg";
}
function _temp2(item_0) {
  return item_0.type === "stay";
}